import type { UseQueryOptions } from "@tanstack/react-query/src/types";
import { useMemo } from "react";
import { useBrandGroupsWithQueries } from "./useBrandGroupsWithQueries";
import { getCookie } from "helpers/cookies";
import { useFeatureRouteParams } from "helpers/shared/hooks/useFeatureRouteParams";
import type { IBrandGroup } from "helpers/transforms/entities";
import { isSuperUser } from "helpers/user";
import type { IEntitiesQuery } from "interfaces/app";
import { useUser } from "users/hooks/useUser";

export function useBrandGroups(
  options?: Omit<
    UseQueryOptions<
      IBrandGroup[],
      unknown,
      IBrandGroup[],
      ["brandGroups", IEntitiesQuery]
    >,
    "queryFn"
  >
) {
  const { data: user } = useUser();
  const email = user?.email;
  const { vendorLevel, vendorLocation } = useFeatureRouteParams();
  const query = useMemo<IEntitiesQuery | null>(() => {
    if (!email || !isSuperUser(email)) {
      return null;
    }
    // Provide as complete a query as possible for fetchEntities, based on current route
    const merchantGroupKey = getCookie("X-Merchant-Group-Id") || "";
    const entityQuery: IEntitiesQuery = { merchantGroupKey };
    if (vendorLevel === "b") {
      entityQuery.brandKey = vendorLocation;
    } else if (vendorLevel === "s") {
      entityQuery.storeKey = vendorLocation;
    }
    return entityQuery;
  }, [email, vendorLevel, vendorLocation]);
  return useBrandGroupsWithQueries(query ?? {}, {
    ...options,
    enabled: !!email && (options?.enabled ?? true),
  });
}
